import { useCallback, useEffect, useState } from 'react';

import { clientConfig } from '@/config/clientConfig';
import { useScenariosStore } from '@/entities/scenario/scenarios.store';
import { ApiBaseError } from '@/shared/api/generated/http-client';
import { useApi } from '@/shared/api/hooks/useApi';
import { useApiMyhub } from '@/shared/apiMyhub/useApiMyhub';

type UseScenariosListProps = { lazy?: boolean };

export const useScenariosList = ({ lazy = false }: UseScenariosListProps = {}) => {
  const { scenariosList, setScenariosList, setPromise } = useScenariosStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiBaseError>();
  const api = useApi();
  const apiMyhub = useApiMyhub();

  const getScenarioList = useCallback(async () => {
    if (clientConfig.isExported) {
      const res = await apiMyhub.getScenarioList();
      if (res) {
        setScenariosList(res);
      }
      return res;
    }

    const cachedScenariosList = useScenariosStore.getState().scenariosList;

    if (cachedScenariosList) {
      return cachedScenariosList;
    }

    let scenarioPromise = useScenariosStore.getState().promise;

    if (!scenarioPromise) {
      setIsLoading(true);
      scenarioPromise = api.scenariosList();
      setPromise(scenarioPromise);
    }

    const response = await scenarioPromise;
    setPromise(null);

    if (response.error) {
      setError(response.error);
    }

    if (!response.data) {
      return undefined;
    }

    setScenariosList(response.data);

    return response.data;
  }, []);

  useEffect(() => {
    const cachedScenariosList = useScenariosStore.getState().scenariosList;
    if (lazy || cachedScenariosList) {
      return;
    }

    getScenarioList();
  }, []);

  return { scenarios: scenariosList, loading: isLoading, error, getScenarioList };
};
